import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc, query, orderBy, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase/firebaseConfig';
import { GroupChatModel, WelcomeMessageModel } from '../types/group';

export class GroupsService {
  private static readonly GROUPS_COLLECTION = 'groups';

  static async loadGroups(): Promise<GroupChatModel[]> {
    try {
      const groupsRef = collection(db, this.GROUPS_COLLECTION);
      const q = query(groupsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const groups: GroupChatModel[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        groups.push({
          id: doc.id,
          name: data.name,
          description: data.description,
          photoUrl: data.photoUrl,
          users: data.users || [],
          createdAt: data.createdAt,
          moderators: data.moderators || [],
          status: data.status,
          messageCount: data.messageCount || 0,
          lastMessageAt: data.lastMessageAt,
        });
      });

      return groups;
    } catch (error) {
      console.error('Error loading groups:', error);
      throw error;
    }
  }

  static async createGroup(group: GroupChatModel): Promise<void> {
    try {
      const groupRef = doc(db, this.GROUPS_COLLECTION, group.id);
      await setDoc(groupRef, {
        name: group.name,
        description: group.description,
        photoUrl: group.photoUrl,
        users: group.users,
        createdAt: group.createdAt,
        moderators: group.moderators,
        status: group.status,
        messageCount: group.messageCount,
      });
    } catch (error) {
      console.error('Error creating group:', error);
      throw error;
    }
  }

  static async updateGroup(group: GroupChatModel): Promise<void> {
    try {
      const groupRef = doc(db, this.GROUPS_COLLECTION, group.id);
      await updateDoc(groupRef, {
        name: group.name,
        description: group.description,
        photoUrl: group.photoUrl,
        status: group.status,
      });
    } catch (error) {
      console.error('Error updating group:', error);
      throw error;
    }
  }

  static async deleteGroup(groupId: string): Promise<void> {
    try {
      const groupRef = doc(db, this.GROUPS_COLLECTION, groupId);
      await deleteDoc(groupRef);
    } catch (error) {
      console.error('Error deleting group:', error);
      throw error;
    }
  }

  static async sendWelcomeMessage(groupId: string, message: WelcomeMessageModel): Promise<void> {
    try {
      const messagesRef = collection(db, this.GROUPS_COLLECTION, groupId, 'messages');
      const messageRef = doc(messagesRef, message.id);
      await setDoc(messageRef, {
        id: message.id,
        content: message.content,
        type: message.type,
        sentBy: message.sentBy,
        createdAt: Timestamp.now(),
        likes: message.likes,
        status: message.status,
        repliesCount: message.repliesCount,
        isModerator: message.isModerator,
      });
    } catch (error) {
      console.error('Error sending welcome message:', error);
      throw error;
    }
  }

  static async uploadGroupImage(file: File, groupId: string): Promise<string> {
    try {
      const storageRef = ref(storage, `groups/${groupId}/${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading group image:', error);
      throw error;
    }
  }
}